// Generated by the Exylon v4 core contract pipeline. Do not edit.
// Source: backend/src/platform/calendar/service-day.ts, copied verbatim.
/**
 * The one service day of the Backend (`docs/decisions/2026-10-08-one-service-day-definition.md`): the Madrid day
 * that starts at 04:00 instead of midnight, so a dinner paid, closed or refunded at 00:40 belongs to the night it was
 * part of. Three features read it, and only they: the Caja drawer day
 * (`docs/decisions/2026-09-30-cash-drawer-sessions.md` point 2), El Parte's «ayer»
 * (`docs/decisions/2026-10-02-el-parte-service-day.md`) and the moment the loyalty month close may run (the months
 * themselves stay calendar months; `docs/decisions/2026-10-11-loyalty-abuse-and-month-close-fixes.md`). Everything
 * else (Analítica, Historial, Workforce, Offers, the loyalty buckets, billing) keeps the calendar day of `./madrid`.
 *
 * The service day of a date D runs from 04:00 of D to 04:00 of D+1, Europe/Madrid: 24 hours, or 23 and 25 at the
 * clock changes (they happen at 02:00 and 03:00, before the boundary). The SQL counterpart is the pair
 * `service.cash_drawer_day(timestamptz)` / `service.cash_drawer_day_start(date)`, which interpolates
 * `SERVICE_DAY_START_HOUR`; an Insights hour-row fold interpolates the same constant.
 *
 * A neutral leaf beside `./madrid`: the API and the worker programs both load it.
 */
import { addCalendarDays, isoWeekdayOfDate, madridLocalTime, madridWallClockInstant, type MadridBusinessDay, type MadridInterval } from './madrid'

/** The Madrid wall-clock hour a service day starts at. */
export const SERVICE_DAY_START_HOUR = 4

/** The same boundary in minutes past the Madrid midnight. */
export const SERVICE_DAY_START_MINUTES = SERVICE_DAY_START_HOUR * 60

/** The service date an instant belongs to: its Madrid date, or the date before when it falls before 04:00. */
export const serviceDateOf = (instant: string | Date): string => {
  const local = madridLocalTime(instant)
  return local.localMinutes < SERVICE_DAY_START_MINUTES ? addCalendarDays(local.localDate, -1) : local.localDate
}

/** The instant the service day of `date` starts: 04:00 Madrid on that date, ISO UTC. */
export const serviceDayStart = (date: string): string => madridWallClockInstant(date, SERVICE_DAY_START_MINUTES)

/** The half-open instant interval of the service day of `date`: [04:00 of `date`, 04:00 of the next date). */
export const serviceDayWindow = (date: string): MadridInterval =>
  Object.freeze({ from_inclusive: serviceDayStart(date), until_exclusive: serviceDayStart(addCalendarDays(date, 1)) })

/** The service day of `date` in the shape of `madridBusinessDay`: its date, ISO weekday and instant bounds. */
export const serviceBusinessDay = (date: string): MadridBusinessDay =>
  Object.freeze({ business_date: date, iso_weekday: isoWeekdayOfDate(date), ...serviceDayWindow(date) })
